<template>
  <div class="FinancesPayoutsExtraPaymentsTable finances-invoices-table" ref="wrapper">
    <PanelHeader
      title="Extra payments"
      :isOpen="show"
      :onToggleButtonClick="togglePanel"
      titleClassName="col-md-8"
      buttonsWrapperClassName="col-md-4"
    >
      <AddItemButton :onClick="addItem">{{addPaymentButtonLabel}}</AddItemButton>
    </PanelHeader>

    <b-collapse
      class="mx-3"
      style="position: relative;"
      v-model="show"
    >
      <EmptyTableMessage v-if="!items.length">
        There are no payments yet. Use "{{addPaymentButtonLabel}}" button.
      </EmptyTableMessage>
      <b-table v-else :fields="fields" :items="items" borderless>
        <template #cell(cost)="{ item, index }">
          <CostInput
            v-model="item.cost"
            :currencyCode="currencyCode"
            :id="`${constInputIdPrefix}-${index}`"
            @input="dataTouched(index, 'cost')"
          />
        </template>
        <template #cell(description)="{ item, index }">
          <b-form-textarea
            id="textarea"
            v-model="item.description"
            rows="2"
            max-rows="6"
            @input="dataTouched(index, 'description')"
          >
          </b-form-textarea>
        </template>
        <template #cell(actions)="data" v-if="items.length >= 1">
          <b-button variant="danger" size="sm w-100" @click.prevent="removeItem(data)">
            <font-awesome-icon icon="trash" />
          </b-button>
        </template>
      </b-table>

      <div v-if="kpiBonuses" class="mb-3">
        <template v-for="(kpiBonus, i) in kpiBonuses">
          <button
            v-if="kpiBonus"
            :key="i"
            type="button"
            class="btn button btn-success mr-3"
            :disabled="isCalculatingPayout"
            @click="addKpiBonus(kpiBonus)">Add KPI bonus ({{ kpiBonus }} {{ currencyCode }})</button>
        </template>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import AddItemButton from '@components/modals/FinanceInvoicesForm/components/AddItemButton.vue'
import EmptyTableMessage from '@components/invoices/EmptyTableMessage.vue'
import PanelHeader from '@components/invoices/form/PanelHeader.vue'
import CostInput from '@components/invoices/form/CostInput.vue'

const defaultItem = {
  'cost': null,
  'description': 'Extra payment'
}

export default {
  components: { PanelHeader, AddItemButton, CostInput, EmptyTableMessage },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    /**
     * $vModel is a vuelidate copy of the passed to the component. We need it to correctly track user input to validate
     * data and mark fields as dirty.
     */
    $vModel: Object,
    currencyCode: {
      type: String,
      default: ''
    },
    kpiBonuses: [Array, null],
    isCalculatingPayout: Boolean,
    addKpiBonus: Function
  },
  data () {
    return {
      show: true,
      addPaymentButtonLabel: 'Add payment',
      constInputIdPrefix: 'extra-payout-cost-input',
      fields: [
        { key: 'cost', label: 'Cost', class: 'cost-column' },
        { key: 'description', label: 'Description', class: 'description-column' },
        { key: 'actions', label: '', class: 'actions-column' }
      ],
      items: []
    }
  },
  mounted () {
    this.setInitialParams()
  },
  watch: {
    'value': {
      deep: true,
      handler () {
        this.setInitialParams()
      }
    }
  },
  methods: {
    togglePanel () {
      this.show = !this.show
    },
    setInitialParams () {
      this.items = this.value || []
      this.triggerInputEmit()
    },
    addItem () {
      this.show = true
      this.items.push(cloneDeep(defaultItem))
      this.$vModel.$touch()

      // Focus Cost input in the added row
      setTimeout(() => {
        const addedCostInputField = this.$refs.wrapper.querySelector(`#${this.constInputIdPrefix}-${this.items.length - 1}`)
        if (addedCostInputField) {
          addedCostInputField.focus()
        }
      }, 0)

      this.triggerInputEmit()
    },
    removeItem (data) {
      const removedIndex = data.index
      this.items = this.items.filter((item, index) => index !== removedIndex)
      this.$vModel.$touch()
      this.dataTouched(data.index, null)
      this.triggerInputEmit()
      this.$emit('dataRemoved', { removedIndex })
    },
    triggerInputEmit () {
      this.$emit('input', this.items)
    },
    dataTouched (index, attrName) {
      this.$emit('dataTouched', { index, attrName })
      this.$vModel.$touch()
    }
  }
}
</script>

<style lang="scss" scoped>
  .FinancesPayoutsExtraPaymentsTable {
    ::v-deep .cost-column {
      min-width: 100px;
    }
  }
</style>
